'use client';

import {
  currentAnalysisAtom,
  entityVerificationDrawerOpenAtom,
  highlightInNoteEntityIndexAtom,
  highlightInNoteHallucinationIndexAtom,
  selectEntityWithTabAtom,
  selectHallucinationWithTabAtom,
  selectedEntityIndexAtom,
  selectedHallucinationIndexAtom,
  showIssueHighlightsAtom,
  showVerifiedHighlightsAtom,
} from '@/features/entity-verification/atoms';
import type { ClinicalNotesAnalysis } from '@/types/entity-verification';
import { findTextPositions } from '@heidi/editor/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { getDefaultStore } from 'jotai';

/*
 * Shared entity-verification decoration leaf. Consumed by thin `Extension.create`
 * twins in each editor system (v1, vMarkdown, markdown-native) — none of which
 * are ever mounted together, so a single plugin key is safe.
 *
 * Architecture note: this ProseMirror plugin reads Jotai atom state via
 * getDefaultStore() instead of React hooks because plugins run outside React's
 * component tree. Atom updates don't trigger a rebuild here — the companion
 * useEntityVerificationSync hook dispatches a transaction carrying this plugin's
 * meta to keep decorations in sync.
 */

export interface EntityMatch {
  from: number;
  to: number;
  type: 'verified' | 'issue';
  entityIndex: number;
  text: string;
  isSelected: boolean;
}

export const entityVerificationPluginKey = new PluginKey('entityVerificationHighlight');

/**
 * Build decorations from entity verification analysis. Exported for testing;
 * the plugin calls it internally with the current Jotai atom snapshot.
 */
export function buildDecorations(
  doc: ProseMirrorNode,
  analysis: ClinicalNotesAnalysis | null,
  selectedEntityIndex: number | null,
  selectedHallucinationIndex: number | null,
  showVerifiedHighlights: boolean,
  showIssueHighlights: boolean,
  highlightInNoteEntityIndex: number | null,
  highlightInNoteHallucinationIndex: number | null
): DecorationSet {
  const decorations: Decoration[] = [];

  if (!analysis?.entity_verification_data) {
    return DecorationSet.empty;
  }

  const { entity_verification, additional_information } =
    analysis.entity_verification_data;

  if (entity_verification) {
    entity_verification.forEach((entity, index) => {
      const isInaccurate =
        entity.accuracy_match === 'inaccurate' || entity.accuracy_match === 'not_present';
      const isSelected = selectedEntityIndex === index;
      const navigatedToThisEntity = highlightInNoteEntityIndex === index;
      const showThisEntity = isInaccurate
        ? showIssueHighlights || navigatedToThisEntity
        : showVerifiedHighlights || navigatedToThisEntity;
      if (!showThisEntity) return;
      const searchText = entity.output_entity || entity.input_entity;
      if (searchText) {
        const positions = findTextPositions(doc, searchText);
        const highlightClass = isInaccurate
          ? 'entity-verification-issue'
          : 'entity-verification-verified';
        positions.forEach(({ from, to }) => {
          decorations.push(
            Decoration.inline(from, to, {
              class: `${highlightClass} ${isSelected ? 'entity-verification-selected' : ''}`,
              'data-entity-type': isInaccurate ? 'issue' : 'verified',
              'data-entity-index': String(index),
            })
          );
        });
      }
    });
  }

  if (additional_information) {
    additional_information.forEach((info, index) => {
      const isSelected = selectedHallucinationIndex === index;
      const navigatedToThis = highlightInNoteHallucinationIndex === index;
      if (!showIssueHighlights && !navigatedToThis) return;
      const searchText = info.output_entity;
      if (searchText) {
        const positions = findTextPositions(doc, searchText);
        positions.forEach(({ from, to }) => {
          decorations.push(
            Decoration.inline(from, to, {
              class: `entity-verification-issue ${isSelected ? 'entity-verification-selected' : ''}`,
              'data-entity-type': 'issue',
              'data-hallucination-index': String(index),
            })
          );
        });
      }
    });
  }

  return DecorationSet.create(doc, decorations);
}

/**
 * Get current state from Jotai atoms
 */
function getAtomState() {
  const store = getDefaultStore();
  return {
    isDrawerOpen: store.get(entityVerificationDrawerOpenAtom),
    analysis: store.get(currentAnalysisAtom),
    selectedEntityIndex: store.get(selectedEntityIndexAtom),
    selectedHallucinationIndex: store.get(selectedHallucinationIndexAtom),
    showVerifiedHighlights: store.get(showVerifiedHighlightsAtom),
    showIssueHighlights: store.get(showIssueHighlightsAtom),
    highlightInNoteEntityIndex: store.get(highlightInNoteEntityIndexAtom),
    highlightInNoteHallucinationIndex: store.get(highlightInNoteHallucinationIndexAtom),
  };
}

/**
 * ProseMirror plugin for entity verification inline highlighting.
 * - Verified entities: solid green underline
 * - Potential issues: wavy orange underline
 *
 * Highlights are shown when the entity verification drawer is open.
 * Clicking on a highlight selects the corresponding entity in the drawer.
 */
export function createEntityVerificationHighlightPlugin({
  enabled,
}: {
  enabled: boolean;
}): Plugin {
  return new Plugin({
    key: entityVerificationPluginKey,
    state: {
      init: (_, { doc }) => {
        if (!enabled) return DecorationSet.empty;

        const state = getAtomState();

        if (!state.isDrawerOpen) return DecorationSet.empty;

        return buildDecorations(
          doc,
          state.analysis,
          state.selectedEntityIndex,
          state.selectedHallucinationIndex,
          state.showVerifiedHighlights,
          state.showIssueHighlights,
          state.highlightInNoteEntityIndex,
          state.highlightInNoteHallucinationIndex
        );
      },
      apply: (tr, oldState) => {
        if (!enabled) return DecorationSet.empty;

        const state = getAtomState();

        if (!state.isDrawerOpen) return DecorationSet.empty;

        if (tr.docChanged || tr.getMeta(entityVerificationPluginKey)) {
          return buildDecorations(
            tr.doc,
            state.analysis,
            state.selectedEntityIndex,
            state.selectedHallucinationIndex,
            state.showVerifiedHighlights,
            state.showIssueHighlights,
            state.highlightInNoteEntityIndex,
            state.highlightInNoteHallucinationIndex
          );
        }

        return oldState.map(tr.mapping, tr.doc);
      },
    },
    props: {
      decorations(state) {
        return this.getState(state);
      },
      handleClick(_view, _pos, event) {
        const target = event.target as HTMLElement;

        if (target.classList.contains('entity-verification-verified')) {
          const entityIndex = target.getAttribute('data-entity-index');
          if (entityIndex !== null) {
            const store = getDefaultStore();
            store.set(selectEntityWithTabAtom, parseInt(entityIndex, 10));
            return true;
          }
        }

        if (target.classList.contains('entity-verification-issue')) {
          const store = getDefaultStore();

          // Inaccurate entities have issue class but data-entity-index (not hallucination-index)
          const entityIndex = target.getAttribute('data-entity-index');
          if (entityIndex !== null) {
            store.set(selectEntityWithTabAtom, parseInt(entityIndex, 10), 'issues');
            return true;
          }

          const hallucinationIndex = target.getAttribute('data-hallucination-index');
          if (hallucinationIndex !== null) {
            store.set(selectHallucinationWithTabAtom, parseInt(hallucinationIndex, 10));
            return true;
          }
        }

        return false;
      },
    },
  });
}
